// TODO исключить из файл из sonar.exclusions
import React, { FC, useCallback, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Divider,
  Modal,
  ModalContent,
  ModalFooter,
  ModalHeader,
  Portal,
  RegularButton,
  Typography,
} from 'fronton-react';
import { useSelector } from 'react-redux';
import { Infobar } from 'common/components/infobar';
import { OperationalTime } from 'common/components/operational-time';
import { RegionChangeDialog } from 'features/region-change-dialog';
import { getRegionId } from 'features/page-model/ducks/get-model';
import { Store } from 'features/stores/types';
import {
  sendInfobarHintShowGA,
  sendInfobarShowGA,
  sendPhoneGA,
  sendStorePageButtonGA,
} from 'features/stores/utils/ga';
import { patchUrlByLocale } from 'utils';
import { SHOP_URL } from 'common/constants';
import { styles } from './style';

type Props = {
  activeRegionId: string;
  store: Store | null;
  onClose: () => void;
};

const StoreInfoMobile: FC<Props> = ({ activeRegionId, store, onClose }) => {
  const {
    id,
    address,
    phone,
    name,
    url,
    subway,
    operationalTime,
    gardenCenterOperationalTime,
    buildingYardOperationalTime,
    pickupOperationalTime,
    importantNotificationTitle,
    importantNotificationDescription,
  } = store as Store;
  const { t } = useTranslation();
  const currentRegionId = useSelector(getRegionId) || '';
  const [changeRegionModal, setChangeRegionModal] = useState(false);
  const differentRegion = activeRegionId !== currentRegionId;
  const shopPage = t('shopPage');
  const workingHours = t('workingHours');

  const sentPhoneGa = useCallback(() => sendPhoneGA(phone, name), [name, phone]);

  const handleINShow = useCallback(
    () => sendInfobarShowGA(name, importantNotificationTitle),
    [name, importantNotificationTitle],
  );

  const handleINHintShow = useCallback(
    () => sendInfobarHintShowGA(name, importantNotificationTitle),
    [name, importantNotificationTitle],
  );

  const handleStorePageButton = useCallback(() => {
    if (differentRegion) setChangeRegionModal(() => true);
    sendStorePageButtonGA(shopPage, name);
  }, [differentRegion, name, shopPage]);

  return (
    <Portal>
      <Modal onClose={onClose} size="m" variant="curtain" data-qa="store-info" show>
        <ModalHeader title={name} />
        <ModalContent>
          {importantNotificationTitle && (
            <Infobar
              message={importantNotificationTitle}
              hint={importantNotificationDescription}
              className={styles.warning}
              onShowBlock={handleINShow}
              onShowHint={handleINHintShow}
            />
          )}
          <Typography variant="m" size="body_short" color="text-primary">
            {address}
          </Typography>
          {subway?.length > 0 && (
            <div className={styles.list}>
              {subway.map((station) => (
                <div key={station.name} className={styles.listItem}>
                  <Typography color="text-secondary" variant="caption">
                    {station.name}
                  </Typography>
                  {station.distance && (
                    <Typography color="text-secondary" variant="caption">
                      {station.distance}
                    </Typography>
                  )}
                </div>
              ))}
            </div>
          )}
          <Divider className={styles.divider} />
          <Typography variant="s" size="subtitle" color="text-primary">
            {workingHours}
          </Typography>
          <div className={styles.timeTable}>
            {operationalTime && (
              <OperationalTime label={`${t('tradeFloor')}:`} timeRange={operationalTime} />
            )}
            {gardenCenterOperationalTime && (
              <OperationalTime label={`${t('gardenCenter')}:`} timeRange={gardenCenterOperationalTime} />
            )}
            {buildingYardOperationalTime && (
              <OperationalTime label={`${t('buildingYard')}:`} timeRange={buildingYardOperationalTime} />
            )}
            {pickupOperationalTime && (
              <OperationalTime label={`${t('pickup')}:`} timeRange={pickupOperationalTime} />
            )}
          </div>
          <Divider className={styles.divider} />
          <Typography variant="s" size="subtitle" color="text-primary">
            {t('forCustomers')}
          </Typography>
          <div className={styles.phoneSection}>
            <Typography variant="s" size="body_short" color="text-primary">
              {t('support')}:
            </Typography>
            <Divider variant="dashed" />
            <a
              href={`tel:${phone}`}
              className={styles.phoneNumber}
              onClick={sentPhoneGa}
              data-qa="contact-number"
            >
              <Typography variant="s" size="body_short">
                {phone}
              </Typography>
            </a>
          </div>
        </ModalContent>
        {url && (
          <>
            <ModalFooter className="">
              {differentRegion ? (
                <RegularButton
                  data-qa="store-info-shop-button"
                  wide
                  variant="outline"
                  size="l"
                  onClick={handleStorePageButton}
                >
                  {shopPage}
                </RegularButton>
              ) : (
                <RegularButton
                  data-qa="store-info-shop-button"
                  wide
                  variant="outline"
                  size="l"
                  onClick={handleStorePageButton}
                  itemProp="url"
                  href={patchUrlByLocale(`${SHOP_URL}/${url}`)}
                  target="_blank"
                >
                  {shopPage}
                </RegularButton>
              )}
            </ModalFooter>
            {differentRegion && (
              <RegionChangeDialog
                active={changeRegionModal}
                closeHandle={() => setChangeRegionModal(false)}
                targetRegionId={activeRegionId}
                targetStoreId={id}
              />
            )}
          </>
        )}
      </Modal>
    </Portal>
  );
};

export { StoreInfoMobile };
